'use client';

import { donationBoxSection } from '@stereoagency/sanity-schema';
import {
  DonationPeriod,
  DonationBoxSection as UIDonationBoxSection
} from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';
import {
  numberFormatterFactory,
  sanityImageAsProps,
  linkAsProps
} from '@/utils';
import { SimpleBlockContent } from '../sanity/client';

type Props = typeof donationBoxSection.schema.donationBoxSection;

const onetimeOptions = [
  { id: '50', value: 50, currency: 'EUR' },
  { id: '250', value: 175, currency: 'EUR' },
  { id: '1500', value: 750, currency: 'EUR' },
  { id: 'other', value: null, currency: null }
];

export function DonationBoxSection({
  title,
  subtitle,
  image,
  donationBoxSettings
}: Props) {
  const { t, lang } = useTranslation('donation-box');
  const [period, setPeriod] = useState(DonationPeriod.ONE_TIME);
  const formatter = numberFormatterFactory(lang, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  });

  const monthlyOptions = [
    {
      id: '15',
      value: 15,
      currency: 'EUR',
      label: t('select-value.per-month')
    },
    {
      id: '30',
      value: 30,
      currency: 'EUR',
      label: t('select-value.per-month')
    },
    {
      id: '100',
      value: 100,
      currency: 'EUR',
      label: t('select-value.per-month')
    },
    {
      id: 'other',
      value: null,
      currency: null,
      label: t('select-value.per-month')
    }
  ];

  const valueOptions =
    period === DonationPeriod.ONE_TIME ? onetimeOptions : monthlyOptions;

  if (
    !image ||
    !donationBoxSettings?.monthlyUrlFormat ||
    !donationBoxSettings.onetimeUrlFormat ||
    !donationBoxSettings.title
  ) {
    return null;
  }

  return (
    <UIDonationBoxSection
      title={title}
      subtitle={subtitle}
      image={sanityImageAsProps(image)}
      donationBox={{
        title: donationBoxSettings.title,
        subtitle: donationBoxSettings.subtitle,
        initialPeriod: DonationPeriod.ONE_TIME,
        valueOptions,
        periodOptions: [
          {
            label: t('periods.one_time'),
            value: DonationPeriod.ONE_TIME
          },
          {
            label: t('periods.monthly'),
            value: DonationPeriod.MONTHLY
          }
        ],
        donationTitle: t('select-value.title'),
        action: donationBoxSettings.additionalAction?.link?.url
          ? {
              label: donationBoxSettings.additionalAction.label,
              ...linkAsProps(donationBoxSettings.additionalAction.link)
            }
          : undefined,
        modal: donationBoxSettings.modalInfo?.title
          ? {
              title: donationBoxSettings.modalInfo.title,
              content: (
                <SimpleBlockContent
                  value={donationBoxSettings.modalInfo.content ?? []}
                />
              )
            }
          : undefined,
        renderValueDisplayValue(value) {
          if (value.value) return formatter.format(value.value);
          return t('select-value.other');
        }
      }}
      onPeriodChange={setPeriod}
      onSelectValue={(id) => {
        const urlFormat =
          period === DonationPeriod.ONE_TIME
            ? donationBoxSettings.onetimeUrlFormat!
            : donationBoxSettings.monthlyUrlFormat!;
        const url = urlFormat.replace(/\{value\}/gi, id);
        window.open(url, '_blank');
      }}
    />
  );
}
